// Service worker registration and the update flow (spec §8.9).
//
// Updates are found three ways: the browser's own check on launch, a check
// whenever the app comes back to the foreground or online, and a version
// compare against the server (which knows the build it is serving). Once a new
// build is installed, the chip offers a reload, and the app also swaps to it by
// itself the next time it goes to the background so the next open is fresh.
import { registerSW } from 'virtual:pwa-register'

export const APP_VERSION: string = __APP_VERSION__

type Listener = () => void
const listeners = new Set<Listener>()
let updateFn: ((reload?: boolean) => Promise<void>) | null = null
let registration: ServiceWorkerRegistration | undefined
let pending = false
let lastCheck = 0

const notify = () => {
  pending = true
  listeners.forEach((l) => l())
}

/** True while a newer build is installed and waiting to take over. */
export const updatePending = () => pending

/** Ask the browser to look for a new service worker; throttled so foreground flaps don't spam. */
export const checkForUpdate = async (force = false): Promise<'pending' | 'checked' | 'unavailable'> => {
  if (!registration) return 'unavailable'
  if (pending) return 'pending'
  if (!force && Date.now() - lastCheck < 60_000) return 'checked'
  lastCheck = Date.now()
  try {
    await registration.update()
  } catch {
    /* offline or SW file unreachable: the next foreground tries again */
  }
  return pending ? 'pending' : 'checked'
}

/**
 * Compare the served build with this one; a mismatch means a deploy happened,
 * so ask for the new worker straight away rather than waiting for the hourly check.
 */
export const compareServerVersion = (serverVersion: string | undefined) => {
  if (!serverVersion || serverVersion === 'dev' || APP_VERSION === 'dev') return
  if (serverVersion !== APP_VERSION) void checkForUpdate(true)
}

const safeToSwap = () => !document.querySelector('[role="dialog"]')

export const registerServiceWorker = () => {
  if (!('serviceWorker' in navigator)) return
  updateFn = registerSW({
    immediate: true,
    onNeedRefresh: notify,
    onRegisteredSW(_url, reg) {
      registration = reg
      if (!reg) return
      setInterval(() => void checkForUpdate(), 60 * 60 * 1000)
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void checkForUpdate()
        // nobody is looking: take the new build now so the next open is already on it
        else if (pending && safeToSwap()) void applySwUpdate()
      })
      window.addEventListener('online', () => void checkForUpdate())
    },
  })
}

export const onSwUpdate = (l: Listener) => {
  listeners.add(l)
  if (pending) l()
  return () => {
    listeners.delete(l)
  }
}

export const applySwUpdate = () => updateFn?.(true) ?? Promise.resolve()

/** Periodic background sync where the platform allows it (installed Chromium). */
export const registerPeriodicSync = async () => {
  if (!('serviceWorker' in navigator)) return
  try {
    const reg = await navigator.serviceWorker.ready
    const periodic = (
      reg as ServiceWorkerRegistration & {
        periodicSync?: { register(tag: string, opts: { minInterval: number }): Promise<void> }
      }
    ).periodicSync
    if (!periodic) return
    const status = await navigator.permissions
      ?.query({ name: 'periodic-background-sync' as PermissionName })
      .catch(() => null)
    if (status && status.state !== 'granted') return
    await periodic.register('alfieos-sync', { minInterval: 60 * 60 * 1000 })
  } catch {
    /* unsupported or denied: the in-app triggers still cover it */
  }
}
